import { api } from './api'
import { API_ENDPOINTS } from '@/config/api'

/**
 * Governance Service
 * Handles proposal creation, retrieval, filtering, and voting operations
 */

export const governanceService = {
  /**
   * List proposals with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.workstream_id - Filter by workstream (optional)
   * @param {string} params.type - Filter by type: poll|budget|decision (optional)
   * @param {string} params.status - Filter by status (optional)
   * @param {boolean} params.mine - Filter to user's proposals (optional)
   * @param {number} params.limit - Pagination limit (default: 20)
   * @param {number} params.offset - Pagination offset (default: 0)
   * @returns {Promise<Object>} List of proposals with pagination info
   */
  async listProposals(params = {}) {
    const queryParams = new URLSearchParams()
    
    if (params.workstream_id) queryParams.append('workstream_id', params.workstream_id)
    if (params.type) queryParams.append('type', params.type)
    if (params.status) queryParams.append('status', params.status)
    if (params.mine) queryParams.append('mine', params.mine)
    if (params.limit) queryParams.append('limit', params.limit)
    if (params.offset) queryParams.append('offset', params.offset)

    const queryString = queryParams.toString()
    const url = queryString 
      ? `${API_ENDPOINTS.governance.proposals}?${queryString}`
      : API_ENDPOINTS.governance.proposals

    const response = await api.get(url)
    return response.data
  },

  /**
   * Get a single proposal by ID
   * @param {string} id - Proposal ID
   * @returns {Promise<Object>} Proposal details including vote tally and user's vote if exists
   */
  async getProposal(id) {
    const response = await api.get(API_ENDPOINTS.governance.getProposal(id))
    return response.data
  },

  /**
   * Create a new proposal
   * @param {Object} data - Proposal data
   * @param {string} data.proposal_type - Type: poll|budget|decision (required)
   * @param {string} data.workstream_id - Workstream ID (optional, for scoped proposals)
   * @param {string} data.title - Proposal title (required)
   * @param {string} data.description - Proposal description (required)
   * @param {Object} data.data - Type-specific payload (required)
   * @param {Array<string>} data.tags - Optional tags for categorization
   * @returns {Promise<Object>} Created proposal object
   */
  async createProposal(data) {
    const response = await api.post(API_ENDPOINTS.governance.createProposal, data)
    return response.data
  },

  /**
   * Cast or update a vote on a proposal
   * @param {string} proposalId - Proposal ID
   * @param {Object} voteData - Vote data
   * @param {string} voteData.vote_option - Vote option: for|against|abstain (required)
   * @param {string} voteData.reason - Optional reason for the vote
   * @returns {Promise<Object>} Vote confirmation with updated tally
   */
  async voteOnProposal(proposalId, voteData) {
    const response = await api.post(
      API_ENDPOINTS.governance.voteOnProposal(proposalId),
      voteData
    )
    return response.data
  },

  /**
   * Delete a proposal by ID
   * @param {string} id - Proposal ID
   * @returns {Promise<Object>} deletion result
   */
  async deleteProposal(id) {
    const response = await api.delete(API_ENDPOINTS.governance.deleteProposal(id))
    return response.data
  },

  /**
   * Get a public poll by ID (no auth)
   * @param {string} id - Poll ID
   * @returns {Promise<Object>} Public poll payload
   */
  async getPublicPoll(id) {
    const response = await api.get(API_ENDPOINTS.public.governancePoll(id))
    return response.data
  },

  /**
   * Public vote endpoint for polls (no auth required)
   * @param {string} pollId
   * @param {Object} data - { name, email, vote_option, reason? }
   */
  async votePublicPoll(pollId, data) {
    const response = await api.post(API_ENDPOINTS.public.governancePollVote(pollId), data)
    return response.data
  },

  /**
   * Helper to format proposal type for display
   * @param {string} type - Proposal type
   * @returns {string} Formatted type label
   */
  formatProposalType(type) {
    const typeMap = {
      poll: 'Poll',
      budget: 'Budget',
      decision: 'Decision'
    }
    return typeMap[type] || type
  },

  /**
   * Helper to format status for display
   * @param {string} status - Proposal status
   * @returns {string} Formatted status label
   */
  formatStatus(status) {
    const statusMap = {
      draft: 'Draft',
      active: 'Active',
      succeeded: 'Passed',
      failed: 'Failed',
      executed: 'Executed',
      canceled: 'Canceled'
    }
    return statusMap[status] || status
  }
}

export default governanceService
